import { HTSClassificationData } from 'types/chat.types';

/**
 * Parses classification result content from markdown text format
 * and extracts structured data for display
 */
export function parseClassificationContent(content: string): {
  classificationId?: string;
  filename?: string;
  data: HTSClassificationData;
} | null {
  try {
    // Extract document ID
    const documentIdMatch = content.match(/Document ID:\s*`([^`]+)`/);
    const classificationId = documentIdMatch ? documentIdMatch[1] : undefined;

    // Extract filename
    const filenameMatch = content.match(/Processing Complete for '([^']+)'/);
    const filename = filenameMatch ? filenameMatch[1] : undefined;

    // Extract HTS Code
    const htsCodeMatch = content.match(/HTS Code:\s*`([^`]+)`/);
    const primary_hts_code = htsCodeMatch ? htsCodeMatch[1] : '';

    // Extract Product Category
    const productCategoryMatch = content.match(/Product Category:\s*\*\*([^*]+)\*\*/);
    const product_category = productCategoryMatch ? productCategoryMatch[1] : undefined;

    // Extract Description (from Primary Classification section)
    const descriptionMatch = content.match(/Description:\s*([^\n•]+)/);
    const hts_description = descriptionMatch ? descriptionMatch[1].trim() : undefined;

    // Extract Confidence
    const confidenceMatch = content.match(/Confidence:\s*(\d+)%/);
    const confidence_score = confidenceMatch ? parseInt(confidenceMatch[1]) / 100 : undefined;

    // Extract Duty Rate
    const dutyRateMatch = content.match(/Duty Rate:\s*([^\n•]+)/);
    const duty_rate = dutyRateMatch ? dutyRateMatch[1].trim() : undefined;

    // Extract Chapter
    const chapterMatch = content.match(/Chapter:\s*(\d+)/);
    const chapter = chapterMatch ? chapterMatch[1] : undefined;

    // Extract Chapter Description
    const chapterDescMatch = content.match(/Chapter:\s*\d+\s*\(([^)]+)\)/);
    const chapter_description = chapterDescMatch ? chapterDescMatch[1] : undefined;

    // Extract Alternative Classifications
    const additional_classifications: Array<{
      hts_code: string;
      description: string;
      confidence: number;
    }> = [];

    const alternativeSection = content.match(/\*\*Alternative Classifications:\*\*([\s\S]*?)(?=\*\*|$)/);
    if (alternativeSection) {
      const altText = alternativeSection[1];
      const altPattern = /HTS Code:\s*`?([^\s`\n]+)`?[\s\n]+Description:\s*([^\n]+)[\s\n]+Confidence:\s*(\d+)%/g;
      let altMatch;
      while ((altMatch = altPattern.exec(altText)) !== null) {
        additional_classifications.push({
          hts_code: altMatch[1],
          description: altMatch[2].trim(),
          confidence: parseInt(altMatch[3]) / 100,
        });
      }
    }

    // Extract Assumptions
    const assumptions: string[] = [];
    const assumptionsSection = content.match(/\*\*Assumptions Made:\*\*([\s\S]*?)(?=\*\*|---)/);
    if (assumptionsSection) {
      const assumptionPattern = /•\s*([^\n]+)/g;
      let assumptionMatch;
      while ((assumptionMatch = assumptionPattern.exec(assumptionsSection[1])) !== null) {
        assumptions.push(assumptionMatch[1].trim());
      }
    }

    // Extract Notes
    const notes: string[] = [];
    const notesSection = content.match(/\*\*Notes:\*\*([\s\S]*?)(?=---|\n\n💬|$)/);
    if (notesSection) {
      const notePattern = /•\s*([^\n]+)/g;
      let noteMatch;
      while ((noteMatch = notePattern.exec(notesSection[1])) !== null) {
        notes.push(noteMatch[1].trim());
      }
    }

    // Extract Total Duty
    const totalDutyMatch = content.match(/Total Duty:\s*([^\n•]+)/);
    const total_duty = totalDutyMatch ? totalDutyMatch[1].trim() : undefined;

    // Extract Tariff Percentage
    const tariffPercentageMatch = content.match(/Tariff Percentage:\s*([^\n•]+)/);
    const tariff_percentage = tariffPercentageMatch ? tariffPercentageMatch[1].trim() : undefined;

    // Extract Tariff Description
    const tariffDescMatch = content.match(/Tariff Description:\s*([^\n•]+)/);
    const tariff_description = tariffDescMatch ? tariffDescMatch[1].trim() : undefined;

    // Extract Tariff Confidence Score
    const tariffConfidenceMatch = content.match(/Tariff Confidence:\s*(\d+)%/);
    const tariff_confidence_score = tariffConfidenceMatch ? parseInt(tariffConfidenceMatch[1]) / 100 : undefined;

    // Extract Jurisdiction
    const jurisdictionMatch = content.match(/Jurisdiction:\s*([^\n•]+)/);
    const jurisdiction = jurisdictionMatch ? jurisdictionMatch[1].trim() : undefined;

    // Extract Source Title
    const sourceTitleMatch = content.match(/Source Title:\s*([^\n•]+)/);
    const source_title = sourceTitleMatch ? sourceTitleMatch[1].trim() : undefined;

    // Extract Source URL
    const sourceUrlMatch = content.match(/Source URL:\s*([^\n•\s]+)/);
    const source_url = sourceUrlMatch ? sourceUrlMatch[1].trim() : undefined;

    // Extract Source Type
    const sourceTypeMatch = content.match(/Source Type:\s*([^\n•]+)/);
    const source_type = sourceTypeMatch ? sourceTypeMatch[1].trim() : undefined;

    // Extract Effective Date
    const effectiveDateMatch = content.match(/Effective Date:\s*([^\n•]+)/);
    const effective_date = effectiveDateMatch ? effectiveDateMatch[1].trim() : undefined;

    // Extract Evidence Quote
    const evidenceQuoteMatch = content.match(/Evidence Quote:\s*"?([^"\n]+)"?/);
    const evidence_quote = evidenceQuoteMatch ? evidenceQuoteMatch[1].trim() : undefined;

    const data: HTSClassificationData = {
      primary_hts_code,
      product_category,
      hts_description,
      confidence_score,
      duty_rate,
      total_duty,
      chapter,
      chapter_description,
      additional_classifications: additional_classifications.length > 0 ? additional_classifications : undefined,
      assumptions: assumptions.length > 0 ? assumptions : undefined,
      notes: notes.length > 0 ? notes : undefined,
      tariff_percentage,
      tariff_description,
      tariff_confidence_score,
      jurisdiction,
      source_title,
      source_url,
      source_type,
      effective_date,
      evidence_quote,
    };

    return {
      classificationId,
      filename,
      data,
    };
  } catch (error) {
    console.error('Failed to parse classification content:', error);
    return null;
  }
}
